import { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import useMailSender from "../../../../hooks/useMailSender.js";
import JumboPayModal from "../../../Reusable/JumboPayModal/JumboPayModal.js";
import PasswordProtectedMessage from "../../../Reusable/FullScreenMessage/PasswordProtectedMessage.js";
import RequestAccessMessage from "../../../Reusable/FullScreenMessage/RequestAccessMessage.js";
import ConfirmUserMessage from "../../../Reusable/FullScreenMessage/ConfirmUserMessage.js";
import queryString from "query-string";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import { useUser } from "../../../../contexts/userContext/userContext.js";
import { useMail } from "../../../../contexts/mailContext/mailContext.js";
import { PAYMENT_ONCE_RESPONSE_ARRAY } from "utils/globalConsts.ts";
import { getLocalStorage } from "utils/globalLocalStorage.js";

export function useMailStatus() {
  const {
    getMailSecurity,
    getPaymentData,
    getMailStatus,
    getUserPasswordInput,
    isMailAvailable,
    getIsDisplayAllowed,
    setIsDisplayAllowed,
    getMailMetaData,
    getFilesDataErrorMessage,
  } = useMail();
  const { getUserData, isLoggedIn } = useUser();
  const [redirectTo, setRedirectTo] = useState();
  const [verifiedSenderMailParams, setVerifiedSenderMailParams] =
    useState(false);
  const { isMailSender } = useMailSender();
  const [fullScreenMessage, setFullScreenMessage] = useState({});
  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const senderMail = searchParams.senderMail;
  const { deleteMailParams, mailId } = useParams();

  const verifiedJumboFormDeleteLinkParam = async () => {
    const parameters = {
      encryptedMailId: mailId,
      hashCodeParam: deleteMailParams,
    };
    const response = await apiRequest(
      "Mails",
      "VerifiedJumboFormDeleteLinkParam",
      parameters
    );
    if (response.status === "success") {
      if (response.data === "ok") {
        setVerifiedSenderMailParams(true);
      }
    }
  };

  useEffect(() => {
    if (deleteMailParams && deleteMailParams !== undefined) {
      verifiedJumboFormDeleteLinkParam();
    }
  }, [deleteMailParams]);

  useEffect(() => {
    switch (getFilesDataErrorMessage()) {
      case "restricted-access": {
        setIsDisplayAllowed(false);
        setFullScreenMessage({
          data: {
            component: () =>
              RequestAccessMessage({
                isEmbedOwnerOnly: getMailMetaData()?.EmbedDownloadOnlyOwner,
              }),
            image: { src: "request-access" },
            center: true,
          },
          isShow: true,
        });

        break;
      }
      case "password-requierd": {
        const includeDeleteParams =
          deleteMailParams && verifiedSenderMailParams;
        if (!getUserPasswordInput() && !includeDeleteParams) {
          setIsDisplayAllowed(false);
          setFullScreenMessage({
            data: {
              title: `mail-password-protected-title`,
              image: { src: "password-folder", ext: "png" },
              component: () =>
                PasswordProtectedMessage({
                  setFullScreenMessage,
                  fullScreenMessage,
                }),
            },
            isShow: true,
          });
        }
        break;
      }
      default:
        break;
    }
  }, [
    getFilesDataErrorMessage(),
    getMailMetaData(),
    getUserPasswordInput(),
    verifiedSenderMailParams,
  ]);

  useEffect(() => {
    setIsDisplayAllowed(true);
    //fetched mail info, verifying mail status before continuing
    if (getMailStatus()) {
      if (isMailAvailable()) {
        //if the authenticated user is the mail sender, allow viewing and skip checks.
        if (!isMailSender()) {
          // checking if mail is recipients only and user in not recipient
          if (
            getMailSecurity()?.recipientsOnly ||
            getMailMetaData()?.EmbedDownloadOnlyOwner
          ) {
            // check if user is not confirmed show him verification component
            if (isLoggedIn() && !getUserData().IsConfirm) {
              setIsDisplayAllowed(false);
              setFullScreenMessage({
                data: {
                  component: () =>
                    ConfirmUserMessage({
                      setFullScreenMessage,
                      fullScreenMessage,
                    }),
                  image: { src: "request-access" },
                  center: true,
                },
                isShow: true,
              });
            } else if (
              getMailSecurity()?.recipientsOnly &&
              getMailMetaData()?.RecipientsList?.includes(
                getUserData()?.UserName
              )
            ) {
              setIsDisplayAllowed(true);
              setFullScreenMessage({
                ...fullScreenMessage,
                isShow: false,
              });
            }
          }
          // jumbopay check
          if (getPaymentData()) {
            //this is one-time-payment mail - check if the mail was paid
            if (
              PAYMENT_ONCE_RESPONSE_ARRAY.includes(getPaymentData().PaymentType)
            ) {
              if (getPaymentData().DenyPreview) {
                //deny if not paid
                if (!getPaymentData().SuccessfulPurchasesCount) {
                  setIsDisplayAllowed(false);
                  setFullScreenMessage({
                    data: {
                      component: JumboPayModal,
                      image: { src: "rabbit-hat-circled" },
                      center: true,
                    },
                    isShow: true,
                  });
                }
              }
            }
            //this is payment-per-user mail - check if the authenticated user has paid
            else {
              if (getPaymentData().DenyPreview) {
                //deny if it the authenticated user did not pay for this mail
                if (
                  !getPaymentData().IsAuthenticatedUserPurchased &&
                  !getPaymentData().AllowDownload
                ) {
                  setIsDisplayAllowed(false);
                  setFullScreenMessage({
                    data: {
                      component: JumboPayModal,
                      image: { src: "rabbit-hat-circled" },
                      center: true,
                    },
                    isShow: true,
                  });
                }
              }
            }
          }
          //password protection check (allowFilesDisplay - to verify that the jumbopay check came out ok)
          else if (
            getIsDisplayAllowed() &&
            getMailSecurity()?.isPasswordProtected
          ) {
            const includeDeleteParams =
              deleteMailParams && verifiedSenderMailParams;

            const isPassowrdExist =
              getUserPasswordInput() ||
              getLocalStorage(`mailPassword-${mailId}`);

            if (!isPassowrdExist && !includeDeleteParams) {
              setIsDisplayAllowed(false);
              setFullScreenMessage({
                data: {
                  title: `mail-password-protected-title`,
                  image: { src: "password-folder", ext: "png" },
                  component: () =>
                    PasswordProtectedMessage({
                      setFullScreenMessage,
                      fullScreenMessage,
                    }),
                },
                isShow: true,
              });
            }
          }
        }
      } else if (getMailStatus() === "removed") {
        //mail was removed
        setIsDisplayAllowed(false);
        setFullScreenMessage({
          data: {
            component: "MailRemovedMessage",
            topBox: {
              text: "mail-status-removed-title",
              icon: { name: "warning", color: "orange" },
              backgroundColor: "yellow",
            },
          },
          isShow: true,
        });
      } else if (getMailStatus() === "violation") {
        //mail marked as blocked (sender has violated terms / mail was reported)
        setIsDisplayAllowed(false);
        setFullScreenMessage({
          data: {
            component: "MailViolationAccessDenied",
            image: { src: "note-pencil", ext: "png" },
          },
          isShow: true,
        });
      } else if (getMailStatus() === "uploading") {
        //mail still in uploading process
        if (isMailSender()) {
          setIsDisplayAllowed(true);
        } else {
          setIsDisplayAllowed(false);
          setFullScreenMessage({
            data: {
              component: "MailUploadingMessage",
              image: { src: "robot", ext: "png" },
            },
            isShow: true,
          });
        }
      } else if (getMailStatus() === "created") {
        setIsDisplayAllowed(false);
        setFullScreenMessage({
          data: {
            title: "mail-status-created-title",
            content: "mail-status-created-content",
            image: { src: "robot", ext: "png" },
          },
          isShow: true,
        });
      } else if (getMailStatus() === "error") {
        //some error with files and they cant be shown
        setIsDisplayAllowed(false);
        setFullScreenMessage({
          data: {
            component: "MailFilesErrorMessage",
            topBox: {
              text: "mail-status-files-error-textbox-title",
              icon: { name: "warning", color: "red" },
              backgroundColor: "white",
            },
          },
          isShow: true,
        });
      } else {
        //mailId is incorrect
        setRedirectTo({ path: "incorrect-mail-id", isError: true });
      }
    }
  }, [getMailStatus(), getUserPasswordInput(), getUserData(), isLoggedIn()]);

  return { redirectTo, fullScreenMessage, setFullScreenMessage };
}
